import React, { ReactElement, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import { useTranslation } from "../../contexts/TranslationContext";
import { FilePool, UploadedFileResponse } from "../../api/types/cms";
import { FormField } from "../../api/types/form";
import { useImageBuilder } from "../../utils/image";
import FilePoolSelect from "../cms/FilePoolSelect";
import { CloseOutlined, StopOutlined } from "@ant-design/icons";
import FileUploadDropZone from "../cms/FileUploadDropZone";
import { CmsFileListRef } from "../cms/CmsFileList";
import CmsFileSelection from "../form/cms/file-selection";
import ScrollContextComponent from "../../contexts/ScrollContext";
import { makeHash } from "../../utils/static";

type FileUploadProps = {
  accept?: string;
  onSuccess?: (
    createdFile: Array<UploadedFileResponse> | UploadedFileResponse | null
  ) => void;
  children?: ReactElement;
  fieldConfig?: FormField;
  uploadToPool?: string;
  contentName?: string;
  small?: boolean;
  directOpen?: boolean;
  hideButton?: boolean;
  hideSearch?: boolean;
  hideFiles?: boolean;
  closeAfterUpload?: boolean;
  onClose?: () => void;
  onRefresh?: () => void;
  multiple?: boolean;
  allowOverwritingFiles?: boolean;
};

export default function FileUpload({
                                     accept,
                                     onSuccess = () => {
                                     },
                                     fieldConfig,
                                     uploadToPool,
                                     contentName,
                                     small = false,
                                     directOpen = false,
                                     hideButton = false,
                                     hideSearch = false,
                                     hideFiles = false,
                                     closeAfterUpload = false,
                                     onClose = () => {
                                     },
                                     onRefresh = () => {
                                     },
                                     multiple = false,
                                     allowOverwritingFiles = true
                                   }: FileUploadProps) {
  const fileListRef = useRef<CmsFileListRef>();
  const { translate } = useTranslation();
  const { buildImageUrl } = useImageBuilder();

  const [newFiles, setNewFiles] = useState<Array<UploadedFileResponse>>([]);

  useEffect(() => {
    if (fieldConfig && fieldConfig.value) {
      console.log(fieldConfig.value);
      setNewFiles(
        Array.isArray(fieldConfig.value)
          ? fieldConfig.value
          : [fieldConfig.value]
      );
    }
  }, [fieldConfig]);

  const [uploadDialog, setUploadDialog] = useState(false);

  const [pool, setPool] = useState<FilePool>();

  useEffect(() => {
    setUploadDialog(directOpen);
  }, [directOpen]);

  const resetFileField = () => {
    setNewFiles([]);
    onSuccess(multiple ? [] : null);
  };

  const handleClose = () => {
    setUploadDialog(false);
    const prevValue = makeHash(
      Array.isArray(fieldConfig?.value)
        ? fieldConfig.value
        : [fieldConfig?.value]
    );
    if (prevValue !== makeHash(newFiles)) {
      onSuccess(multiple ? newFiles : newFiles?.at(0) ?? null);
    }
    onClose();
  };

  const renderImagePreview = () => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10
        }}>
        {newFiles.map(file => (
          <img
            key={file?.file_id}
            src={buildImageUrl(file, null, 30, false)}
            alt={file?.file_id}
            style={{
              alignSelf: "center",
              alignItems: "center",
              alignContent: "center"
            }}
          />
        ))}
      </div>
    );
  };

  const renderFileInfo = () => {
    return newFiles.map((file, index) => (
      <Typography
        key={index}
        variant="caption"
        className="cutTextEnd"
        flexShrink={1}>
        {typeof file !== "string" ? file?.file_id : file}
      </Typography>
    ));
  };

  return (
    <>
      {!hideButton && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          width="100%">
          <Button
            size={small ? "small" : "medium"}
            variant="outlined"
            sx={{
              flex: 1,
              height: 40,
              justifyContent: "flex-start"
            }}
            onClick={() => setUploadDialog(true)}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
              width="100%">
              {newFiles?.length > 0 && fieldConfig ? (
                <>
                  {/image\/.*/.test(
                    fieldConfig.attr?.["accept"]
                  ) && renderImagePreview()}
                  {renderFileInfo()}
                </>
              ) : (
                <Typography variant="overline">
                  {translate("form.general.file_select")}
                </Typography>
              )}
            </Stack>
          </Button>
          <Button
            startIcon={<StopOutlined />}
            size={small ? "small" : "medium"}
            color="warning"
            onClick={resetFileField}
            sx={{ height: 40 }}>
            {translate("form.general.reset")}
          </Button>
        </Stack>
      )}
      <Dialog
        fullWidth
        open={uploadDialog}
        maxWidth="lg"
        onClose={handleClose}>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}>
            <Stack spacing={1} flex={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}>
                  <Typography variant="subtitle1">
                    {`${translate(
                      "pages.cms.file_upload.pool_selection"
                    )}:`}
                  </Typography>
                  <FilePoolSelect
                    selectedPoolId={
                      uploadToPool ?? pool?.id
                    }
                    onPoolChange={pool =>
                      setPool(
                        pool.id === "all"
                          ? undefined
                          : pool
                      )
                    }
                    disabled={Boolean(uploadToPool)}
                  />
                </Stack>
              </Stack>
            </Stack>
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <ScrollContextComponent
          component="div"
          spacing={3}
          sx={{
            overflow: "scroll",
            height: "100%",
            width: "100%",
            position: "relative",
            p: 2
          }}>
          <FileUploadDropZone
            value={fieldConfig?.value}
            uploadToPool={uploadToPool ?? pool?.id}
            onRefresh={() => {
              fileListRef.current?.load();
              onRefresh();
            }}
            onClose={onClose}
            accept={accept}
            contentName={contentName}
            minHeight={hideFiles ? "50vh" : 200}
            allowOverwritingFiles={allowOverwritingFiles}
          />
          {!hideFiles && (
            <CmsFileSelection
              listRef={fileListRef}
              hideSearch={hideSearch}
              poolId={uploadToPool ?? pool?.id}
              preSelected={newFiles}
              onFileSelectionUpdate={setNewFiles}
              multiple={multiple}
            />
          )}
        </ScrollContextComponent>
      </Dialog>
    </>
  );
}
